import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAppDispatch } from '../../store/hooks';
import { setUser } from '../../store/slices/userSlice';
import authService, { AuthResponse, ValidateUserResponse } from '../../services/authService';
import {
  getLoginAuthSettings,
  formatUsernameInput,
  normalizeUsernameForSubmit,
  validateUsername,
  LOGIN_FORM_AUTOCOMPLETE,
} from '../../config/authConfig';
import {
  LOGIN_GO_KEYBOARD_PROPS,
  useLoginFieldFocus,
  handleEnterOrGoKeyDown,
} from '../../utils/loginKeyboard';
import { consumeSocialAuthError } from '../../utils/socialAuth';
import { trackEvent } from '../../utils/analytics';
import AuthMessage from '../AuthMessage/AuthMessage';
import PasswordField from '../common/PasswordField/PasswordField';
import SocialLoginButtons from '../SocialLoginButtons/SocialLoginButtons';
import './LoginForm.css';

interface LoginFormProps {
  onLoginSuccess?: () => void;
  compact?: boolean; // For modal use
  onStepChange?: (step: number) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess, compact = false, onStepChange }) => {
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<number>(1);
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [otp, setOtp] = useState<string[]>(['', '', '', '']);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [isPartners, setIsPartners] = useState<boolean>(false);
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const formRef = useRef<HTMLFormElement>(null);
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const otpFirstRef = useRef<HTMLInputElement>(null);

  // Check if current subdomain is "partners"
  useEffect(() => {
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0]?.toLowerCase();
    setIsPartners(subdomain === 'partners');
  }, []);

  // Timer countdown effect
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  useLoginFieldFocus(usernameRef, step === 1 && !loading);
  useLoginFieldFocus(passwordRef, step === 2 && !isMobile && !loading);
  useLoginFieldFocus(otpFirstRef, step === 2 && isMobile && !loading);

  useEffect(() => {
    const socialError = consumeSocialAuthError();
    if (socialError) {
      setError(socialError);
    }
  }, []);

  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  const loginAuth = getLoginAuthSettings(isPartners);

  const handleValidateUser = async () => {
    const validationError = validateUsername(username, loginAuth);
    if (validationError) {
      setError(validationError);
      return;
    }

    const normalizedUsername = normalizeUsernameForSubmit(username, loginAuth);
    if (normalizedUsername !== username) {
      setUsername(normalizedUsername);
    }

    setLoading(true);
    setError('');

    try {
      const response: ValidateUserResponse = await authService.validateUser(normalizedUsername);
      
      if (response.success) {
        const mobileFlow = !loginAuth.emailOnly && (response.isMobile || false);
        setIsMobile(mobileFlow);
        if (response.isPartners !== undefined) {
          setIsPartners(response.isPartners);
        }
        setStep(2);
        // Start cooldown timer if mobile (use value from API or default to 30)
        if (mobileFlow) {
          const cooldownSeconds = response.otpCooldownSeconds || 30;
          setResendCooldown(cooldownSeconds);
        }
      } else {
        setError(response.error || 'User validation failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) {
      return;
    }
    if (step === 1) {
      await handleValidateUser();
    } else {
      await handleLogin(e);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const normalizedUsername = normalizeUsernameForSubmit(username, loginAuth);

    try {
      const otpValue = isMobile ? otp.join('') : password;
      const response = await authService.login(normalizedUsername, otpValue);
      
      if (response.success) {
        trackEvent('sign_in', { method: isMobile ? 'otp' : 'password' });
        // Try to use user data from login response first
        if (response.user) {
          dispatch(setUser(response.user));
        } else {
          // If not in response, fetch user info and store in Redux
          // Retry getCurrentUser if it fails (cookie might not be set immediately)
          let userFetched = false;
          let retries = 0;
          const maxRetries = 3;
          
          while (!userFetched && retries < maxRetries) {
            try {
              const userResponse = await authService.getCurrentUser();
              if (userResponse.success && userResponse.user) {
                dispatch(setUser(userResponse.user));
                userFetched = true;
              } else if (retries < maxRetries - 1) {
                // Wait a bit before retrying
                await new Promise(resolve => setTimeout(resolve, 500));
                retries++;
              } else {
                retries++;
              }
            } catch (err) {
              console.error('Error fetching user after login:', err);
              if (retries < maxRetries - 1) {
                // Wait a bit before retrying
                await new Promise(resolve => setTimeout(resolve, 500));
                retries++;
              } else {
                retries++;
              }
            }
          }
        }
        
        // Call success callback after Redux is updated
        // Add a small delay to ensure Redux state has propagated to all components
        if (onLoginSuccess) {
          setTimeout(() => {
            onLoginSuccess();
          }, 200);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSuccess = async (response: AuthResponse) => {
    if (response.user) {
      dispatch(setUser(response.user));
    } else {
      try {
        const userResponse = await authService.getCurrentUser();
        if (userResponse.success && userResponse.user) {
          dispatch(setUser(userResponse.user));
        }
      } catch (err) {
        console.error('Error fetching user after social login:', err);
      }
    }
    if (onLoginSuccess) {
      setTimeout(() => {
        onLoginSuccess();
      }, 200);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 3) {
      const nextInput = document.getElementById(`login-digit-${index + 2}`);
      nextInput?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`login-digit-${index}`);
      prevInput?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    const digits = pastedData.replace(/[^0-9]/g, '').slice(0, 4).split('');
    
    if (digits.length > 0) {
      const newOtp = [...otp];
      digits.forEach((digit, idx) => {
        if (idx < 4) {
          newOtp[idx] = digit;
        }
      });
      setOtp(newOtp);
      
      // Focus on the last filled input or the last input if all are filled
      const lastFilledIndex = Math.min(digits.length - 1, 3);
      const nextInput = document.getElementById(`login-digit-${lastFilledIndex + 1}`);
      nextInput?.focus();
    }
  };

  return (
    <div className={`login-form-wrapper ${compact ? 'compact' : ''}`}>
      {!compact && (
        <h2 className="login-form-title text-center">
          {isPartners ? 'Partner Login/Register' : 'Login/Register with Peppar'}
        </h2>
      )}

      <form ref={formRef} onSubmit={handleFormSubmit} className="login-form-body" autoComplete="on">
        {step === 1 ? (
          <div id="login-step-1">
            {error && <AuthMessage variant="error">{error}</AuthMessage>}
            <SocialLoginButtons
              onSuccess={handleSocialSuccess}
              onError={setError}
              disabled={loading}
            />
            {!compact && <h5>{loginAuth.usernameLabel}</h5>}
            {compact && (
              <label className="login-field-label" htmlFor="login-username">
                {loginAuth.usernameLabel}
              </label>
            )}
            <input
              ref={usernameRef}
              type={loginAuth.emailOnly ? 'email' : 'text'}
              name={step === 1 ? 'username' : undefined}
              id="login-username"
              className="login-input"
              placeholder={loginAuth.usernamePlaceholder}
              autoComplete={LOGIN_FORM_AUTOCOMPLETE.username}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              inputMode={loginAuth.emailOnly ? 'email' : 'text'}
              autoFocus
              {...LOGIN_GO_KEYBOARD_PROPS}
              value={username}
              onChange={(e) => setUsername(formatUsernameInput(e.target.value, loginAuth))}
              onKeyDown={(e) => handleEnterOrGoKeyDown(e, formRef)}
              disabled={loading}
            />
            <button
              type="submit"
              className="login-btn-primary"
              disabled={loading}
            >
              {loading ? 'Loading…' : compact ? 'Continue' : 'Continue'}
            </button>
          </div>
        ) : (
          <div id="login-step-2">
            {compact ? (
              <div className="login-step-header">
                <button
                  type="button"
                  className="login-back-btn"
                  onClick={() => { setStep(1); setError(''); setPassword(''); setOtp(['', '', '', '']); }}
                  aria-label="Go back"
                >
                  <i className="fa fa-arrow-left" aria-hidden="true"></i>
                </button>
                <div className="login-step-account">
                  <span className="login-step-account-label">Continue as</span>
                  <span className="login-step-account-value">{username}</span>
                </div>
              </div>
            ) : (
              <div className="login-go-back" onClick={() => { setStep(1); setError(''); }}>
                <i className="fa fa-long-arrow-left" aria-hidden="true"></i> Back
              </div>
            )}

            {error && <AuthMessage variant="error">{error}</AuthMessage>}

            {isMobile ? (
              <div className="login-otp-input-digits">
                <h5>Please enter the OTP sent to your phone</h5>
                <div className="login-digit-group">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={index === 0 ? otpFirstRef : undefined}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      id={`login-digit-${index + 1}`}
                      className="login-otp-input"
                      maxLength={1}
                      value={digit}
                      {...(index === 3 ? LOGIN_GO_KEYBOARD_PROPS : { enterKeyHint: 'next' as const })}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        handleOtpChange(index, value);
                      }}
                      onKeyDown={(e) => {
                        handleOtpKeyDown(index, e);
                        if (index === 3) {
                          handleEnterOrGoKeyDown(e, formRef);
                        }
                      }}
                      onPaste={handleOtpPaste}
                    />
                  ))}
                </div>
                <h6 className="login-resend-otp">
                  Didn't get an OTP?{' '}
                  {resendCooldown > 0 ? (
                    <span className="login-resend-disabled">
                      Resend OTP in {resendCooldown}s
                    </span>
                  ) : (
                    <a 
                      className="login-resend-link" 
                      href="#" 
                      onClick={(e) => { 
                        e.preventDefault(); 
                        if (!loading) {
                          handleValidateUser(); 
                        }
                      }}
                    >
                      Re-send OTP
                    </a>
                  )}
                </h6>
              </div>
            ) : (
              <div className="login-password-login">
                {!compact && <h5>Please enter your password to login</h5>}
                <input
                  type="text"
                  name="username"
                  value={normalizeUsernameForSubmit(username, loginAuth)}
                  autoComplete={LOGIN_FORM_AUTOCOMPLETE.username}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                  className="login-autofill-bridge"
                />
                <label className="login-field-label" htmlFor="login-password">
                  Password
                </label>
                <PasswordField
                  ref={passwordRef}
                  id="login-password"
                  name="password"
                  inputClassName="login-input"
                  required
                  placeholder="Enter your password"
                  autoComplete={LOGIN_FORM_AUTOCOMPLETE.currentPassword}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  {...LOGIN_GO_KEYBOARD_PROPS}
                  onKeyDown={(e) => handleEnterOrGoKeyDown(e, formRef)}
                  disabled={loading}
                />
                {!compact && (
                  <div className="login-forgot-password">
                    <Link to="/forgotPassword">Forgot your password?</Link>
                  </div>
                )}
              </div>
            )}

            <button type="submit" className="login-btn-primary" disabled={loading}>
              {loading ? 'Logging in…' : compact ? 'Log in' : 'LOGIN'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default LoginForm;

